"use client";

import React from "react";
import { motion } from "framer-motion";
import { Calculator, Stethoscope, Scale, Wrench, Zap, ShoppingBag, Cpu, HeartPulse, Store, UserCheck, Building2, ArrowRight, CircleCheck as CheckCircle2, Briefcase } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const sectors = [{
  id: 1,
  title: "Accountants & Financial Services",
  description: "Attract high-value tax, audit, and advisory clients with targeted local search and authoritative content strategies.",
  icon: Calculator,
  linkText: "Explore Financial SEO",
  linkUrl: "/ServicesPage",
  badge: "Financial"
}, {
  id: 2,
  title: "Dentists & Dental Practices",
  description: "Dominate local maps and organic search for high-value cosmetic treatments, Invisalign, and dental implants.",
  icon: Stethoscope,
  linkText: "Explore Dental SEO",
  linkUrl: "/ServicesPage",
  badge: "Healthcare"
}, {
  id: 3,
  title: "Solicitors & Law Firms",
  description: "Capture qualified enquiries for conveyancing, family law, corporate legal services, and dispute resolution.",
  icon: Scale,
  linkText: "Explore Legal SEO",
  linkUrl: "/ServicesPage",
  badge: "Legal"
}, {
  id: 4,
  title: "Independent Retailers & Shops",
  description: "Drive high footfall and local buying intent for boutique shops, specialty showrooms, and town centre retail spaces.",
  icon: Store,
  linkText: "Explore Retail SEO",
  linkUrl: "/LocalSEOPage",
  badge: "Retail"
}, {
  id: 5,
  title: "Ecommerce & Online Retailers",
  description: "Scale organic revenue through technical site architecture fixes, faceted navigation, and category optimisation.",
  icon: ShoppingBag,
  linkText: "Explore Ecommerce SEO",
  linkUrl: "/EcommerceSeoPage",
  badge: "E-Commerce"
}, {
  id: 6,
  title: "B2B Tech & Corporate Services",
  description: "Align your search visibility with complex buying journeys to drive qualified demo requests and high-contract leads.",
  icon: Cpu,
  linkText: "Explore B2B SEO",
  linkUrl: "/B2BSeoLeadGenerationPage",
  badge: "B2B & Tech"
}, {
  id: 7,
  title: "Consultants & Professional Advisors",
  description: "Position your solo consultancy or specialist practice as the go-to authority with thought-leadership and niche search capture.",
  icon: UserCheck,
  linkText: "Explore Consultant SEO",
  linkUrl: "/ServicesPage",
  badge: "Advisory"
}, {
  id: 8,
  title: "Small Businesses & Local Startups",
  description: "Establish a formidable local search presence, outrank legacy competitors, and generate predictable incoming enquiries.",
  icon: Building2,
  linkText: "Explore Small Business SEO",
  linkUrl: "/LocalSEOPage",
  badge: "Small Business"
}, {
  id: 9,
  title: "Plumbers & Heating Engineers",
  description: "Secure high-intent boiler installation, servicing, and emergency repair leads across your primary service zones.",
  icon: Wrench,
  linkText: "Explore Trades SEO",
  linkUrl: "/ServicesPage",
  badge: "Trades"
}, {
  id: 10,
  title: "Electricians & Contractors",
  description: "Generate consistent commercial contracts, EV charger queries, and domestic electrical testing enquiries.",
  icon: Zap,
  linkText: "Explore Contractor SEO",
  linkUrl: "/ServicesPage",
  badge: "Trades"
}, {
  id: 11,
  title: "Private Medical & Health Clinics",
  description: "Build clinical authority and patient trust for private medical specialists, physiotherapy clinics, and wellness centres.",
  icon: HeartPulse,
  linkText: "Explore Clinic SEO",
  linkUrl: "/ServicesPage",
  badge: "Healthcare"
}];
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function Industry() {
  return <section id="industrysectorsgrid" data-nav="light" className="relative py-20 lg:py-28 bg-background text-foreground overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none overflow-hidden aria-hidden:true">
        <div className="absolute -top-24 right-10 w-96 h-96 bg-secondary/40 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-10 left-10 w-80 h-80 bg-primary/5 rounded-full blur-3xl opacity-50" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary bg-primary/5 gap-1.5">
            <Briefcase className="size-3.5" />
            Specialised Industry Expertise
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-serif mb-6">
            Who I Help in Bedford
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Whether you run a high-street shop in Bedford town centre, a trade firm in Kempston, or a growing business across Wixams, Elstow, wider Bedfordshire, or nearby Milton Keynes, I build bespoke SEO strategies tailored to your exact industry and commercial objectives.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {sectors.map((sector, i) => {
          const IconComponent = sector.icon;
          return <motion.div key={sector.id} variants={itemVariants} data-index={i}>
                <Card className="h-full flex flex-col justify-between border-border/80 bg-card hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] duration-300">
                  <CardHeader className="space-y-3 pb-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="p-2.5 rounded-lg bg-primary/10 text-primary inline-flex items-center justify-center">
                        <IconComponent className="size-5 shrink-0" />
                      </div>
                      <Badge variant="secondary" className="text-2xs font-normal bg-muted text-muted-foreground">
                        {sector.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg font-semibold text-foreground font-serif pt-1">
                      {sector.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 pt-0 flex-1 flex flex-col justify-between">
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {sector.description}
                    </CardDescription>

                    <div className="pt-2">
                      <Link to={sector.linkUrl} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 transition-colors group">
                        <span>{sector.linkText}</span>
                        <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="mt-16 rounded-2xl bg-card border border-border p-8 lg:p-10 shadow-sm relative overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8 space-y-3">
              <div className="inline-flex items-center gap-2 text-primary font-medium text-sm">
                <CheckCircle2 className="size-4" />
                <span>Direct Consultant Access & Tailored Strategy</span>
              </div>
              <h3 className="text-2xl font-bold font-serif text-foreground">
                Don't See Your Specific Industry Listed?
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                I regularly work directly with independent consultants, specialised contractors, and ambitious businesses throughout Bedford, Kempston, Wixams, Elstow, Bedfordshire, and Milton Keynes. Book a consultation with me to discuss your sector's specific growth opportunities.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-end">
              <Button size="lg" className="w-full sm:w-auto" asChild>
                <Link to="/ContactPage">
                  Book a Free Consultation
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" className="w-full sm:w-auto" asChild>
                <Link to="/CaseStudiesPage">
                  View Real Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}